import { Button, Form, Modal } from 'antd';
import { FC, useState, useEffect } from 'react';
import { FloatInput } from '../Fields/FloatInput';
import { yupSync } from '../../utils';
import { FloatTextArea } from '../Fields/FloatTextArea';
import { Loader } from '../Loader/Loader';
import { tenderDataProps } from '../../pages/TenderViewPage/types';
import { useCreateSupportingDocumentMutation } from '../../api/tenders';

import { useUpdateTaskMutation } from '../../api/tenders';

import close from '../../assets/RoundedClose.svg';

import * as Yup from 'yup';
import { usePreventSpaceTyping } from '../../hooks/usePreventSpaceTyping';

interface MessageApi {
    success: (message: string) => void;
    error: (message: string) => void;
}

interface ModalProps {
    openModal: boolean;
    setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
    setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
    messageApi?: MessageApi;
    type?: string;
    data?: tenderDataProps;
    taskId?: number | null | string;
}

interface FormDatatoSend {
    name: any;
    comment: string | null;
    document_name: string;
    action: string;
    link: string;
}

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required('This is a required field')
        .test('name', 'This is a required field', (value) => {
            const passwordRegex = /[^\s]/;
            return passwordRegex.test(value);
        }),
    link: Yup.string()
        .required('This is a required field')
        .matches(/^(https?:\/\/)/, 'Oops! Your link is not correct')
});

export const NewDocumentModal: FC<ModalProps> = ({
    openModal,
    setOpenModal,
    messageApi,
    type = 'create',
    data,
    setTrigger,
    taskId
}) => {
    const { handleKeyPress } = usePreventSpaceTyping();

    const [createSupportingDoc, { data: createSupportingDocData, isSuccess, isError, isLoading }] =
        useCreateSupportingDocumentMutation();
    const [updateTask, { isLoading: isLoadingUpdate }] = useUpdateTaskMutation();

    const [name, setName] = useState('');

    const [form] = Form.useForm();
    const [isConfirmModal, setIsConfirmModal] = useState(false);

    useEffect(() => {
        if (type === 'update') {
            if (!taskId || !data) return;
            const element = data.phase_tasks.find((elem) => elem.id === taskId);

            if (element) {
                form.setFieldsValue({
                    //@ts-ignore
                    name: element?.name,
                    link: element.link,
                    comment: element.comments.tender_representative
                });
                setName(element.name);
            }
        }
    }, [taskId, taskId]);

    const createOrUpdateNewDocumentFunc = async (values: FormDatatoSend) => {
        const formData = {
            ids: {
                tenderId: data?.id
            },
            data: {
                document_name: values.name.trim(),
                comment: values.comment ? values.comment : '',
                action: 'design_document',
                link: values.link
            }
        };

        try {
            if (type === 'create') {
                await createSupportingDoc(formData);
            }
            if (type === 'update') {
                const updateData = {
                    ids: {
                        tender_id: data?.id,
                        task_id: taskId
                    },
                    data: {
                        _method: 'PUT',
                        document_name: values.name.trim(),
                        comment: values.comment ? values.comment : '',
                        action: 'design_document',
                        link: values.link
                    }
                };
                await updateTask(updateData);
            }

            await setTrigger((prev: boolean) => !prev);
            setOpenModal(false);
            await messageApi?.success('The task has been created successfully');
        } catch {
            messageApi?.error('Something went wrong, please try again');
        }
    };

    const handleFormSubmit = () => {
        form.validateFields()
            .then((values: any) => {
                createOrUpdateNewDocumentFunc(values);
            })
            .catch(() => {});
    };

    return (
        <>
            <Modal
                className="modal-modal"
                destroyOnClose
                centered
                open={openModal}
                onOk={() => handleFormSubmit()}
                onCancel={() => {
                    if (form.isFieldsTouched()) {
                        setIsConfirmModal(true);
                    } else {
                        setOpenModal(false);
                    }
                }}
                closeIcon={<img src={close} />}
                // width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button
                            block
                            key="back"
                            onClick={() => {
                                if (form.isFieldsTouched()) {
                                    setIsConfirmModal(true);
                                } else {
                                    setOpenModal(false);
                                }
                            }}
                            className="cancel"
                        >
                            Cancel
                        </Button>
                        <Button block key="submit" type="primary" onClick={isLoading ? () => {} : handleFormSubmit}>
                            {isLoading ? <Loader /> : 'Save'}
                        </Button>
                    </div>
                }
            >
                <div className="modal">
                    <div className="modal__header meeting" style={{ height: '88px' }}>
                        <div className="modal__title">{type === 'update' ? 'Resend Document' : 'New Document'}</div>
                    </div>

                    <div className="modal__main main" style={{ padding: '24px 56px 43px 56px' }}>
                        <Form form={form} onFinish={handleFormSubmit}>
                            <Form.Item
                                name="name"
                                className="input-wrapper"
                                rules={yupSync('name', validationSchema, true)}
                            >
                                <FloatInput
                                    disabled={name ? true : false}
                                    floatLabel={'Document Name'}
                                    required
                                    className="input"
                                    maxLength={60}
                                    value={name}
                                />
                            </Form.Item>
                            <Form.Item name="link" rules={yupSync('link', validationSchema, true)}>
                                <FloatTextArea
                                    floatLabel={'Link to Document'}
                                    maxLength={1024}
                                    required={true}
                                    onKeyPress={handleKeyPress}
                                    hasError={() => !!form.getFieldError(['link']).length}
                                />
                            </Form.Item>
                            <Form.Item name="comment">
                                <FloatTextArea floatLabel={'Comment'} maxLength={255} hasError={() => {}} />
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </Modal>
            <Modal
                className="confirmModal"
                centered
                open={isConfirmModal}
                closeIcon={<div style={{ display: 'none', cursor: 'context-menu' }}></div>}
                width={600}
                footer={
                    <div style={{ display: 'flex', justifyItems: 'space-between', gap: '16px', width: '100%' }}>
                        <Button key="back" onClick={() => setIsConfirmModal(false)} block className="cancel">
                            Cancel
                        </Button>
                        <button
                            className="quit"
                            onClick={() => {
                                setOpenModal(false);
                                setIsConfirmModal(false);
                            }}
                        >
                            Quit
                        </button>
                    </div>
                }
            >
                <div className="modal-confirm">Your data won’t be saved! Are you sure you want to quit?</div>
            </Modal>
        </>
    );
};
